import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Search, X, CornerDownLeft } from "lucide-react";
import { getEntity } from "@/lib/registry";
import { StatusBadge } from "@/components/library/badges";
import { EmptyState } from "@/components/library/states";

export function SearchBar({ placeholder = "Sök i allt…", onClick, onChange, value, shortcut = true, testid = "search-bar" }) {
  return (
    <button type="button" onClick={onClick} data-testid={testid}
      className="flex items-center gap-2 w-full sm:w-72 bg-secondary/60 border border-border rounded-xl px-3 py-2 text-sm text-muted-foreground hover:border-[hsl(var(--border-hover))] transition-colors">
      <Search className="h-4 w-4" />
      <span className="flex-1 text-left truncate">{value || placeholder}</span>
      {shortcut && <kbd className="hidden sm:inline-flex items-center gap-0.5 text-[0.65rem] font-mono px-1.5 py-0.5 rounded bg-background border border-border">⌘K</kbd>}
    </button>
  );
}

// FilterBar — configurable chips. Pure presentation; parent owns state.
export function FilterBar({ filters = [], active = {}, onToggle, testid = "filter-bar" }) {
  return (
    <div data-testid={testid} className="flex items-center gap-2 flex-wrap">
      {filters.map((f) => {
        const isActive = active[f.group] === f.value;
        return (
          <button key={`${f.group}:${f.value}`} onClick={() => onToggle?.(f.group, f.value)}
            data-testid={`filter-${f.group}-${f.value}`}
            className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-colors ${
              isActive ? "bg-accent text-white border-accent" : "bg-secondary/60 text-muted-foreground border-border hover:text-foreground"}`}>
            {f.label}
          </button>
        );
      })}
    </div>
  );
}

// GlobalSearch — ⌘K modal. Results grouped by entity_type (one index for all + AI).
export function GlobalSearch({ open, onClose, query, onQuery, groups = {}, onSelect, testid = "global-search" }) {
  const types = Object.keys(groups);
  const total = types.reduce((n, t) => n + groups[t].length, 0);
  return (
    <AnimatePresence>
      {open && (
        <div className="fixed inset-0 z-50 flex items-start justify-center pt-[12vh] px-4" data-testid={testid}>
          <motion.div className="absolute inset-0 bg-black/60" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} onClick={onClose} />
          <motion.div className="relative w-full max-w-xl bg-background border border-border rounded-2xl shadow-2xl overflow-hidden"
            initial={{ opacity: 0, y: -12, scale: 0.98 }} animate={{ opacity: 1, y: 0, scale: 1 }} exit={{ opacity: 0, y: -12 }} transition={{ duration: 0.2 }}>
            <div className="flex items-center gap-3 px-4 border-b border-border">
              <Search className="h-4 w-4 text-muted-foreground" />
              <input autoFocus value={query} onChange={(e) => onQuery?.(e.target.value)} data-testid="global-search-input"
                placeholder="Sök uppgifter, manualer, problem, recept…"
                className="flex-1 bg-transparent py-3.5 text-sm outline-none placeholder-muted-foreground" />
              <button onClick={onClose} className="text-muted-foreground hover:text-foreground"><X className="h-4 w-4" /></button>
            </div>
            <div className="max-h-[50vh] overflow-y-auto p-2">
              {total === 0 ? (
                <div className="p-6"><EmptyState title="Inga träffar" hint="Sökmotorn indexerar alla entiteter — och används även av AI." /></div>
              ) : (
                types.map((t) => {
                  const cfg = getEntity(t);
                  return (
                    <div key={t} className="mb-2">
                      <p className="overline text-muted-foreground px-3 py-2 flex items-center gap-2">
                        {cfg.icon && <cfg.icon className="h-3.5 w-3.5" />}{cfg.plural}
                      </p>
                      {groups[t].map((r) => (
                        <button key={r.ref_id} onClick={() => onSelect?.(t, r)} data-testid={`search-result-${r.ref_id}`}
                          className="w-full flex items-center justify-between gap-3 px-3 py-2 rounded-lg hover:bg-secondary text-left transition-colors group">
                          <span className="flex items-center gap-2 min-w-0">
                            {cfg.icon && <cfg.icon className="h-4 w-4 text-muted-foreground shrink-0" />}
                            <span className="text-sm truncate">{r.title}</span>
                          </span>
                          <span className="flex items-center gap-2">
                            {r.status && <StatusBadge entityType={t} status={r.status} />}
                            <CornerDownLeft className="h-3.5 w-3.5 text-muted-foreground opacity-0 group-hover:opacity-100" />
                          </span>
                        </button>
                      ))}
                    </div>
                  );
                })
              )}
            </div>
            <div className="px-4 py-2 border-t border-border text-[0.7rem] text-muted-foreground font-mono">
              Ett index · används av både användare och AI
            </div>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
}
